import {specialBodiesSlices, entities} from '../../util/uiconstants';
import {normalize, truthyWinsMergeStrategy} from '../../util/utils';
import Utility from '../../util/utilityfunctions';
import {flatten} from 'lodash';

export function normalizeScots(data, state = {}) {
	const scots = data.map((scot) => ({
		...scot,
		itApplications: scot.itApplications?.map((itApplication) => itApplication.id),
		controls: scot.controls?.map((control) => control.id)
	}));

	const controls = data
		.flatMap((scot) => scot.controls || [])
		.map((control) => {
			const relatedScots = scots.filter((scot) => scot.controls?.some((id) => id === control.id)).map((scot) => scot.id);

			return {
				...control,
				itApplications: control.itApplications?.map((itApplication) => itApplication.id) || [],
				scots: relatedScots || []
			};
		});

	return {
		...state,
		scots: normalize(scots, 'id', state.scots, truthyWinsMergeStrategy, /* replace -> */ true),
		itApplications: normalize(
			flatten(
				data
					// Scots do have itApplications related
					.map((scot) => scot.itApplications)
					// Controls do have itApplications related, but duplicated entries are going to be taken care of by the normalize function
					.concat(flatten(data.map((scot) => scot.controls?.map((control) => control.itApplications))))
			),
			'id',
			state.itApplications,
			truthyWinsMergeStrategy
		),
		controls: normalize(controls, 'id', state.controls, truthyWinsMergeStrategy)
	};
}

export function normalizeItApplications(data, state = {}) {
	const controls = normalize(flatten(data.map((itApplication) => itApplication?.controls || [])), undefined, state.controls, truthyWinsMergeStrategy);
	const scots = data
		.flatMap((itApplication) => itApplication.scots || [])
		.map((scot) => {
			const _scot = {
				...scot,
				controls: [...new Set(scot.scotControls || [])]
			};
			delete _scot.scotControls;
			return _scot;
		});
	return {
		...state,
		itApplications: normalize(
			data.map((itApplication) => ({
				...itApplication,
				scots: itApplication.scots?.map((scot) => scot.scotId),
				controls: itApplication.controls?.map((control) => control.id),
				itProcesses: itApplication.itProcesses?.map((itProcess) => itProcess.id),
				selected: false,
				hasAssociation: false
			})),
			undefined,
			state.itApplications,
			truthyWinsMergeStrategy
		),
		scots: normalize(scots, 'scotId', state.scots, truthyWinsMergeStrategy),
		controls: controls,
		itProcesses: normalize(flatten(data.map((itApplication) => itApplication.itProcesses)), undefined, state.itProcesses, truthyWinsMergeStrategy)
	};
}

export function normalizeITSOApplications(data, state = {}) {
	const controls = normalize(flatten(data.map((itsoApplication) => itsoApplication?.controls || [])), undefined, state.controls, truthyWinsMergeStrategy);
	const scots = data
		.flatMap((itsoApplications) => itsoApplications.scots || [])
		.map((scot) => {
			const _scot = {
				...scot,
				controls: [...new Set(scot.scotControls || [])]
			};
			delete _scot.scotControls;
			return _scot;
		});
	return {
		...state,
		itsoApplications: normalize(
			data.map((itsoApplications) => ({
				...itsoApplications,
				scots: itsoApplications.scots?.map((scot) => scot.scotId),
				controls: itsoApplications.controls?.map((control) => control.id),
				itProcesses: itsoApplications.itProcesses?.map((itProcess) => itProcess.id),
				selected: false,
				hasAssociation: false
			})),
			undefined,
			state.itsoApplications,
			truthyWinsMergeStrategy
		),
		scots: normalize(scots, 'scotId', state.scots, truthyWinsMergeStrategy),
		controls: controls,
		itProcesses: normalize(flatten(data.map((itsoApplications) => itsoApplications.itProcesses)), undefined, state.itProcesses, truthyWinsMergeStrategy)
	};
}

export function normalizeItProcesses(data, state = {}) {
	// IT Risks and ITControls are comming denormalized in the response. This is for nesting them accordingly (itRisk -> itControl)
	const itProcessesList = data.map((itProcess) => {
		itProcess.itRisks = buildItRisksForItProcess(itProcess);

		return itProcess;
	});

	const allItRisks = flatten(itProcessesList.map((itProcess) => itProcess.itRisks));
	const allItControls = flatten(itProcessesList.map((itProcess) => itProcess.itControls));
	const allItApplications = flatten(itProcessesList.map((itProcess) => itProcess.itApplications));
	const itProcesses = normalize(
		itProcessesList.map((itProcess) => ({
			...itProcess,
			itApplications: itProcess.itApplications?.map((itApplication) => itApplication.id),
			// Filter itApplications based on isSupportingIT field for related IT applications
			relatedItApplications: itProcess.itApplications?.filter((app) => !app.isSupportingIT)?.map((itApplication) => itApplication.id) || [],
			relatedTasks: itProcess.relatedTasks?.map((relatedTask) => relatedTask.id),
			controls: itProcess.controls?.map((control) => control.id),
			itRisks: itProcess.itRisks?.map((itRisk) => itRisk.id),
			itControls: itProcess.itControls?.map((itControl) => itControl.id),
			selected: false,
			hasAssociation: false
		})),
		undefined,
		state.itProcesses,
		truthyWinsMergeStrategy
	);

	return {
		...state,
		itProcesses,
		itApplications: normalize(allItApplications.filter(Boolean), undefined, state.itApplications, truthyWinsMergeStrategy),
		itRisks: normalize(
			allItRisks.map((itRisk) => ({
				...itRisk,
				itControls: itRisk.itControls?.map((itCtrl) => itCtrl.id) || []
			})),
			undefined,
			state.itRisks,
			truthyWinsMergeStrategy
		),
		itControls: normalize(allItControls, undefined, state.itControls, truthyWinsMergeStrategy),
		relatedTasks: normalize(flatten(itProcessesList.map((itProcess) => itProcess.relatedTasks)), undefined, state.relatedTasks, truthyWinsMergeStrategy),
		controls: normalize(flatten(itProcessesList.map((itProcess) => itProcess.controls)), undefined, state.controls, truthyWinsMergeStrategy)
	};
}

export function normalizeItControls(data, state = {}, replace = false) {
	return {
		...state,
		itControls: normalize(data ? data.data : [], undefined, state.itControls, truthyWinsMergeStrategy, replace)
	};
}

export function normalizeRiskTypes(data, state = {}) {
	return {
		...state,
		riskTypes: normalize(data, undefined, state.riskTypes, truthyWinsMergeStrategy)
	};
}

function riskMergeStrategy(id, element, result) {
	truthyWinsMergeStrategy(id, element, result);
	//Get all risks doesn't set entityId and entityName, so we need to set them here to avoid errors in RelatedObjectListIterator children display.
	let _risk = result?.byId[id];
	_risk.entityId = entities.Risk;
	_risk.entityName = _risk.riskName;
}

export function normalizeRisks(data, state = {}) {
	return {
		...state,
		risks: normalize(data, undefined, state.risks, riskMergeStrategy)
	};
}

export function normalizeRiskFactors(data, state = {}, replace = false, mergeStrategy = truthyWinsMergeStrategy) {
	const _data = data?.map((riskFactor) => {
		return {
			...riskFactor,
			riskId: riskFactor.riskId || null
		};
	});
	return {
		...state,
		riskFactors: normalize(_data, undefined, state.riskFactors, mergeStrategy, replace)
	};
}

//Task entity has guid string as entityUId, so we need to avoid converting it to number.
function parseUid(id) {
	return typeof id === 'number' ? Number(id) : id;
}

export function normalizeEntities(data, state = {}, parentMergeStrategy = truthyWinsMergeStrategy, childNormalizer = normalizeEntities) {
	if (data.length === 0) {
		return {
			...state
		};
	}
	const sliceName = specialBodiesSlices[data.find((entity) => entity.entityId).entityId];

	const value = data.map((entity) => {
		const childEntity = entity.children?.find((child) => {
			return child.entityId;
		});

		let returnValue = {...entity, id: parseUid(entity.entityUId)};
		if (childEntity) {
			// Each entity may have a different set of children
			const childrenPropertyName = entity.parentEntityId ? `${entity.parentEntityId}_${entity.parentEntityUId}_children` : 'children';

			// Task entity id is a string (taskId), so we need to avoid converting the id to a number (if any other entity type is a string, add to the below condition)
			const isNumericEntityUId = childEntity.entityId !== entities.Task;

			returnValue = {
				...returnValue,
				[specialBodiesSlices[childEntity.entityId]]: entity.children.map((child) => (isNumericEntityUId ? parseUid(child.entityUId) : child.entityUId)),
				[childrenPropertyName]: entity.children.map((child) => {
					return {entityId: child.entityId, entityUId: child.entityUId};
				})
			};

			if (entity.parentEntityId) {
				delete returnValue.children;
			}
		}
		return returnValue;
	});
	let normalizedData = {
		...state,
		[sliceName]: normalize(value, undefined, state[sliceName], parentMergeStrategy)
	};
	const children = data
		.filter((entity) => entity.children?.length > 0)
		.flatMap((entity) =>
			entity.children.map((child) => {
				return {
					parentEntityId: entity.entityId,
					parentEntityUId: entity.entityUId,
					...child
				};
			})
		);
	return childNormalizer(children, normalizedData);
}

export function normalizeControls(control, state = {}) {
	const existingControl = state.controls?.byId[control.id];
	if (!existingControl) {
		return {
			...state,
			controls: {
				...state.controls,
				byId: {
					...state.controls?.byId,
					[control.id]: control
				}
			}
		};
	}

	const mergedControl = {
		...existingControl,
		controlDisplayName: control.controlDisplayName ?? existingControl.controlDisplayName,
		relatedRisks: control.relatedRisks ?? existingControl.relatedRisks,
		scots: existingControl?.scots?.concat(control.scots?.filter((scot) => !existingControl.scots.some((existingScot) => existingScot.id === scot.id))),
		relatedDeficiencies: control?.relatedDeficiencies ? [...control.relatedDeficiencies] : []
	};

	return {
		...state,
		controls: {
			...state.controls,
			byId: {
				...state.controls.byId,
				[control.id]: mergedControl
			}
		}
	};
}

export function normalizeAccounts(account, state = {}) {
	const existingAccount = state.controls?.byId[account.id];
	if (!existingAccount) {
		return {
			...state,
			accounts: {
				...state.accounts,
				byId: {
					...state.accounts?.byId,
					[account.id]: account
				}
			}
		};
	}

	return {
		...state,
		accounts: {
			...state.accounts,
			byId: {
				...state.accounts.byId,
				[account.id]: account
			}
		}
	};
}

// Given a denormalized list of itRisks and itControls, returns a normalized (nested) version of the data
function buildItRisksForItProcess(itProcess) {
	const normalizedItRisks = [];
	const itProcessItRisks = itProcess?.itRisks;
	const itProcessItControls = itProcess?.itControls;

	if (itProcessItRisks && itProcessItRisks.length > 0 && itProcessItControls) {
		for (const itRisk of itProcessItRisks) {
			itRisk.itProcessName = itProcess.itProcessName;
			itRisk.itControls = itProcessItControls.filter((itCtrl) => itCtrl.itRiskId === itRisk.id);
			normalizedItRisks.push(itRisk);
		}
	} else if (itProcessItRisks && itProcessItRisks.length > 0) {
		for (const itRisk of itProcessItRisks) {
			normalizedItRisks.push(itRisk);
		}
	}

	return normalizedItRisks;
}

const mapStrategies = {
	[entities.SCOT]: (data, state = {}) => {
		return normalizeScots(data, state);
	},
	[entities.ItSoApplication]: (data, state = {}) => {
		return state;
	},
	[entities.ItProcess]: (data, state = {}) => state,
	[entities.ItControl]: (data, state = {}) => state
};

const defaultStrategy = (_, state) => state;

/**
 * Gets the specified body type Id mapping strategy.
 * @param {number} entityId The entity id.
 * @returns {Function} A strategy function for mapping.
 */
export function getMapStrategy(entityId) {
	return mapStrategies[entityId] || defaultStrategy;
}

function controlMergeStrategy(id, element, result) {
	let childEntityId = element?.children?.length > 0 ? element.children[0].entityId : undefined;
	let stateChildren = result?.byId[id]?.children || [];
	truthyWinsMergeStrategy(id, element, result);

	if (stateChildren.length > 0) {
		result.byId[id].children = [
			...result.byId[id].children,
			//The same control may come multiple times but with different children type entities.
			//Truthy will override the whole children set with whathever comes from result.
			//By doing this spread operator on the previous state value, we can keep the overriden childs which entity id was different
			//This will allow us to have different entity type children co existing with each other.
			...stateChildren.filter((child) => {
				return (
					//The child relationship is new (avoid duplicates)
					result.byId[id].children.find((item) => Utility.deepEqual(child, item)) === undefined &&
					//The child entity was overriden
					(!childEntityId || child.entityId !== childEntityId)
				);
			})
		];
	}
}

const mergeStrategies = {
	[entities.Control]: controlMergeStrategy
};

export function getMergeStrategy(entityId) {
	return mergeStrategies[entityId] || truthyWinsMergeStrategy;
}

export function controlChildrenNormalize(data, normalizedState) {
	const scots = data.filter((entity) => entity.entityId === entities.SCOT);
	const risks = data.filter((entity) => entity.entityId === entities.Risk);

	if (scots?.length > 0) {
		normalizedState = normalizeEntities(scots, normalizedState);
	}

	if (risks?.length > 0) {
		normalizedState = normalizeEntities(risks, normalizedState);
	}

	return normalizedState;
}

const normalizer = {
	[entities.Control]: controlChildrenNormalize
};

export function getChildrenNormalize(entityId) {
	return normalizer[entityId] || normalizeEntities;
}
